import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { ConfirmUserSuspensionDialog } from '@modules/users/components/UsersTable/ConfirmUserSuspensionDialog';
import { useUpdateUserState, useUpdateUserType } from '@modules/users/hooks';
import { UserRowRoleType } from '@modules/users/store/types';
import { trackUserManagementEvents } from '@modules/workspaceSettings/utils';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  Tooltip,
} from '@mui/material';
import { UserState } from 'generated/graphql';
import { useState } from 'react';
import { UserRoleMenuItems } from '../UserRoleMenuItems';

interface ActiveUserRoleColumnCellActionsProps {
  userID: string;
  email: string;
  userRole: UserRowRoleType;
}

export const ActiveUserRoleColumnCellActions = ({
  userID,
  email,
  userRole,
}: ActiveUserRoleColumnCellActionsProps) => {
  const { openModal, closeModal } = useMuiModalContext();

  const [roleMenuAnchorEl, setRoleMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const { updateUserType, isLoading: isUpdatingUserType } = useUpdateUserType();
  const { updateUserState, isLoading: isUpdatingUserState } =
    useUpdateUserState();

  const closeMenu = () => {
    setRoleMenuAnchorEl(null);
  };

  const handleSuspendUser = () => {
    const onConfirm = () => {
      updateUserState(userID, UserState.Suspended, email);
      closeModal();
      trackUserManagementEvents({
        eventName: 'User Management - Suspended User From Workspace',
        userEmail: email,
      });
    };

    openModal({
      contentComponent: () => (
        <ConfirmUserSuspensionDialog
          email={email}
          onCancel={() => closeModal()}
          onConfirm={onConfirm}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  };

  const convertUserRole = (newRole: UserRowRoleType) => {
    updateUserType(userID, newRole, email);
    closeMenu();
  };

  if (isUpdatingUserType || isUpdatingUserState) {
    return (
      <Box height="98%" position="absolute" top={0} right={10}>
        <CircularProgress size={34} />
      </Box>
    );
  }

  return (
    <>
      <Tooltip title="Change User Role" placement="top">
        <IconButton
          onClick={(event) => setRoleMenuAnchorEl(event.currentTarget)}
        >
          <SupervisorAccountRoundedIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
            }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={roleMenuAnchorEl}
        open={Boolean(roleMenuAnchorEl)}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': 'user-role-menu',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{ sx: { maxWidth: 500 } }}
      >
        <UserRoleMenuItems onChange={convertUserRole} selectedRole={userRole} />
      </Menu>
      <Tooltip title="Suspend User" placement="top">
        <IconButton color="error" onClick={handleSuspendUser}>
          <PersonRemoveRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
};
